import ModuleTabs from '@/components/common/ModuleTab';
import { xplorSpotTitle, xplorTraderTitle } from '@/helpers/environmentHelper';
import { useAppSelector } from '@/state/hooks';
import { selectAccountType } from '@/state/reducers/authSlice';
import useLanguage from '@/utils/hooks/useLanguage';
import { Tab } from '@headlessui/react';
import React, { useMemo, useState } from 'react';
import StepButtons from '../shared/StepButtons';
import StepContent from '../shared/StepContent';
import StepLayout from '../shared/StepLayout';

interface TourStepProps {
    isActive: boolean;
    onNext: () => void;
    onPrev: () => void;
    onSkip: () => void;
    progress: number;
}

interface OnboardingStepProps extends TourStepProps {
    copyKey: string;
    stepContentKey: keyof ReturnType<typeof useLanguage>['onboardingSteps'] | string;
    targetSelector?: string | string[];
    desktopPosition?: 'top' | 'right' | 'bottom' | 'left' | 'center';
    horizontalPadding?: number;
    canGoBack?: boolean;
    isLastStep?: boolean;
}

const OnboardingStep: React.FC<OnboardingStepProps> = ({
    isActive,
    onNext,
    onPrev,
    onSkip,
    copyKey,
    stepContentKey,
    targetSelector,
    desktopPosition = 'right',
    horizontalPadding,
    canGoBack = true,
    isLastStep = false
}) => {
    const lang = useLanguage();
    const accountType = useAppSelector(selectAccountType);

    const copy = useMemo(() => lang.onboarding[copyKey] || { title: '', description: '' }, [lang, copyKey]);
    const onboardingCopy = lang.onboardingSteps[stepContentKey];
    const content = useMemo(
        () => lang.onboardingStepContent[copyKey] || { intro: '', highlights: [] as string[], note: '' },
        [lang, copyKey]
    );

    const platformTitle = useMemo(() => {
        switch (accountType) {
            case 'MARGIN':
                return xplorTraderTitle;
            default:
                return xplorSpotTitle;
        }
    }, [accountType]);

    const title = useMemo(() => {
        if (copyKey === 'welcome') {
            return `${copy.title} ${platformTitle}`;
        }
        if (['bankWallet'].includes(copyKey)) {
            if (accountType === 'MARGIN') return copy.titleTrader;
            else return copy.titleSpot;
        }
        return copy.title;
    }, [accountType, platformTitle, copy]);

    const description = useMemo(() => {
        if (['welcome', 'bankWallet'].includes(copyKey)) {
            if (accountType === 'MARGIN') return copy.descriptionTrader;
            else return copy.descriptionSpot;
        }
        return copy.description;
    }, [accountType, copy]);

    return (
        <StepLayout
            title={title}
            description={description}
            isActive={isActive}
            targetSelector={targetSelector}
            desktopPosition={desktopPosition}
            horizontalPadding={horizontalPadding}>
            <div className="space-y-4">
                <RenderStepContent
                    accountType={accountType}
                    stepContentKey={stepContentKey}
                    onboardingCopy={onboardingCopy}
                    content={content}
                />
                <StepButtons
                    canGoBack={canGoBack}
                    canGoForward={true}
                    onNext={onNext}
                    onPrev={onPrev}
                    onSkip={onSkip}
                    isLastStep={isLastStep}
                />
            </div>
        </StepLayout>
    );
};

export default OnboardingStep;

interface RenderStepContentProps {
    accountType: 'NOP' | 'MARGIN';
    stepContentKey: string;
    onboardingCopy: any;
    content: any;
}

const RenderStepContent = ({ accountType, stepContentKey, onboardingCopy, content }: RenderStepContentProps) => {
    const [activeModuleTab, setActiveModuleTab] = useState<'add' | 'reorder' | 'resize'>('add');
    const [activeOrderFormTab, setActiveOrderFormTab] = useState<'general' | 'tips' | 'settings'>('general');
    const [activeTickersTab, setActiveTickersTab] = useState<'general' | 'tips' | 'settings'>('general');

    switch (stepContentKey) {
        case 'stepWelcome':
            return (
                <div className="space-y-4">
                    <StepContent>
                        {accountType === 'MARGIN' ? (
                            <p className="">{onboardingCopy.introTrader}</p>
                        ) : (
                            <p className="">{onboardingCopy.introSpot}</p>
                        )}
                    </StepContent>
                    <div className="space-y-1 rounded-lg bg-brand-neutral-800 px-4">
                        {onboardingCopy.highlights.map((item) => (
                            <div key={item} className="flex items-start gap-3">
                                <span className="min-w-fit font-bold text-brand-primary-light">✓</span>
                                <p className="text-sm text-brand-neutral-300">{item}</p>
                            </div>
                        ))}
                    </div>
                    <div className="text-sm text-brand-neutral-300">
                        <p className="mt-4">{onboardingCopy.footer}</p>
                    </div>
                    <StepContent highlighted>
                        <p className="text-xs italic">{onboardingCopy.note}</p>
                    </StepContent>
                </div>
            );
        case 'stepDeposit':
            return (
                <div className="border-y border-brand-neutral-700">
                    <div className="space-y-3 text-sm py-3">
                        <StepContent>
                            {accountType === 'MARGIN' ? (
                                <p className="mb-3">{onboardingCopy.depositsIntroTrader}</p>
                            ) : (
                                <p className="mb-3">{onboardingCopy.depositsIntroSpot}</p>
                            )}
                        </StepContent>
                        <div className="text-brand-neutral-300 space-y-3 text-sm">
                            {(accountType === 'MARGIN'
                                ? onboardingCopy.depositFlowTrader
                                : onboardingCopy.depositFlowSpot
                            ).map((method, index) => (
                                <p key={`deposit-flow-${index}`}>
                                    {index + 1}. {method}
                                </p>
                            ))}
                        </div>
                    </div>
                </div>
            );
        case 'stepWithdrawal':
            return (
                <div className="border-y border-brand-neutral-700">
                    <div className="space-y-3 text-sm py-3">
                        <StepContent>
                            {accountType === 'MARGIN' ? (
                                <p className="mb-3">{onboardingCopy.withdrawalsIntroTrader}</p>
                            ) : (
                                <p className="mb-3">{onboardingCopy.withdrawalsIntroSpot}</p>
                            )}
                        </StepContent>
                        <div className="text-brand-neutral-300 space-y-3 text-sm">
                            {(accountType === 'MARGIN'
                                ? onboardingCopy.withdrawalFlowTrader
                                : onboardingCopy.withdrawalFlowSpot
                            ).map((method, index) => (
                                <p key={`withdrawal-flow-${index}`}>
                                    {index + 1}. {method}
                                </p>
                            ))}
                        </div>
                    </div>
                </div>
            );
        case 'stepBankWallet':
            return (
                <div className="space-y-4 border-y border-brand-neutral-700 py-3">
                    <Tab.Group defaultIndex={0}>
                        <ModuleTabs.List className="border-none p-0">
                            <ModuleTabs.Child className="bg-brand-neutral-600">
                                {onboardingCopy.bankAccountsTab}
                            </ModuleTabs.Child>
                            {accountType === 'NOP' && (
                                <ModuleTabs.Child className="bg-brand-neutral-600">
                                    {onboardingCopy.walletsTab}
                                </ModuleTabs.Child>
                            )}
                        </ModuleTabs.List>
                        <Tab.Panels className="">
                            <Tab.Panel>
                                <StepContent>
                                    <p>{onboardingCopy.bankIntro}</p>
                                </StepContent>
                                <div className="pt-3 space-y-3">
                                    {onboardingCopy.bankSteps.map((step, index) => (
                                        <div key={'bank-' + index} className="text-sm text-brand-neutral-300">
                                            {index + 1}. {step}
                                        </div>
                                    ))}
                                </div>
                            </Tab.Panel>
                            {accountType === 'NOP' && (
                                <Tab.Panel>
                                    <StepContent>
                                        <p>{onboardingCopy.walletIntro}</p>
                                    </StepContent>
                                    <div className="pt-3 space-y-3">
                                        {onboardingCopy.walletItems.map((item, index) => (
                                            <div key={'wallet-' + index} className="text-sm text-brand-neutral-300">
                                                {index + 1}. {item}
                                            </div>
                                        ))}
                                    </div>
                                    <StepContent highlighted>
                                        <p className="text-xs">💡 {onboardingCopy.walletNote}</p>
                                    </StepContent>
                                </Tab.Panel>
                            )}
                        </Tab.Panels>
                    </Tab.Group>
                </div>
            );
        case 'stepHelpSupport': {
            return (
                <div className="space-y-4 border-y border-brand-neutral-700">
                    <div className="space-y-3 text-sm py-3">
                        <p className="text-brand-neutral-300">{onboardingCopy.completionBody}</p>
                    </div>
                </div>
            );
        }
        case 'orderForm': {
            return (
                <div className="space-y-4 border-y border-brand-neutral-700">
                    <div className="space-y-3 text-sm py-3">
                        <div className="overflow-hidden rounded-lg border border-brand-neutral-600">
                            <button
                                type="button"
                                className="w-full px-3 py-2 text-left text-sm font-semibold text-brand-neutral-200"
                                onClick={() => setActiveOrderFormTab('general')}>
                                {content.generalTab}
                            </button>
                            {activeOrderFormTab === 'general' && (
                                <div className="border-t border-brand-neutral-600 p-3">
                                    <div className="space-y-3 text-sm">
                                        <div className="text-brand-neutral-200 rounded space-y-2">
                                            {content.generalSteps.map((step, index) => (
                                                <p key={`general-step-${index}`}>
                                                    • <span className="">{step}</span>
                                                </p>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="overflow-hidden rounded-lg border border-brand-neutral-600">
                            <button
                                type="button"
                                className="w-full px-3 py-2 text-left text-sm font-semibold text-brand-neutral-200"
                                onClick={() => setActiveOrderFormTab('tips')}>
                                {content.tipsTab}
                            </button>
                            {activeOrderFormTab === 'tips' && (
                                <div className="border-t border-brand-neutral-600 p-3">
                                    <div className="space-y-3 text-sm">
                                        <div className="text-brand-neutral-200 rounded space-y-2">
                                            {content.tipsSteps.map((step, index) => (
                                                <p key={`tips-step-${index}`}>
                                                    • <span className="">{step}</span>
                                                </p>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="overflow-hidden rounded-lg border border-brand-neutral-600">
                            <button
                                type="button"
                                className="w-full px-3 py-2 text-left text-sm font-semibold text-brand-neutral-200"
                                onClick={() => setActiveOrderFormTab('settings')}>
                                {content.settingsTab}
                            </button>
                            {activeOrderFormTab === 'settings' && (
                                <div className="border-t border-brand-neutral-600 p-3">
                                    <div className="space-y-3 text-sm">
                                        <div className="text-brand-neutral-200 rounded space-y-2">
                                            <p>
                                                • <span className="">{content.settingsSteps}</span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
        }
        case 'tickers': {
            return (
                <div className="space-y-4 border-y border-brand-neutral-700">
                    <div className="space-y-3 text-sm py-3">
                        <div className="overflow-hidden rounded-lg border border-brand-neutral-600">
                            <button
                                type="button"
                                className="w-full px-3 py-2 text-left text-sm font-semibold text-brand-neutral-200"
                                onClick={() => setActiveTickersTab('general')}>
                                {content.generalTab}
                            </button>
                            {activeTickersTab === 'general' && (
                                <div className="border-t border-brand-neutral-600 p-3">
                                    <div className="space-y-3 text-sm">
                                        <div className="text-brand-neutral-200 rounded space-y-2">
                                            {content.generalSteps.map((step, index) => (
                                                <p key={`general-step-${index}`}>
                                                    • <span className="">{step}</span>
                                                </p>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="overflow-hidden rounded-lg border border-brand-neutral-600">
                            <button
                                type="button"
                                className="w-full px-3 py-2 text-left text-sm font-semibold text-brand-neutral-200"
                                onClick={() => setActiveTickersTab('tips')}>
                                {content.tipsTab}
                            </button>
                            {activeTickersTab === 'tips' && (
                                <div className="border-t border-brand-neutral-600 p-3">
                                    <div className="space-y-3 text-sm">
                                        <div className="text-brand-neutral-200 rounded space-y-2">
                                            {content.tipsSteps.map((step, index) => (
                                                <p key={`tips-step-${index}`}>
                                                    • <span className="">{step}</span>
                                                </p>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="overflow-hidden rounded-lg border border-brand-neutral-600">
                            <button
                                type="button"
                                className="w-full px-3 py-2 text-left text-sm font-semibold text-brand-neutral-200"
                                onClick={() => setActiveTickersTab('settings')}>
                                {content.settingsTab}
                            </button>
                            {activeTickersTab === 'settings' && (
                                <div className="border-t border-brand-neutral-600 p-3">
                                    <div className="space-y-3 text-sm">
                                        <div className="text-brand-neutral-200 rounded space-y-2">
                                            <p>
                                                • <span className="">{content.settingsSteps}</span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
        }
        default: {
            let intro = content.intro;

            if (!content.intro) {
                if (accountType === 'MARGIN') intro = content.introTrader;
                else intro = content.introSpot;
            }

            return (
                <div className="border-y border-brand-neutral-700">
                    <div className="space-y-4 text-sm text-brand-neutral-300 py-3">
                        {intro && (
                            <StepContent>
                                <p>{intro}</p>
                            </StepContent>
                        )}
                        {content.highlights && (
                            <div className="space-y-3">
                                {content.highlights.map((highlight) => (
                                    <div
                                        key={highlight}
                                        className="rounded-sm border-l-2 border-brand-primary bg-brand-neutral-800 p-3 py-1.5">
                                        <p>{highlight}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                        {content.note && (
                            <StepContent highlighted>
                                <p className="text-xs">💡 {content.note}</p>
                            </StepContent>
                        )}
                        {content.footer && <p>{content.footer}</p>}
                    </div>
                </div>
            );
        }
    }
};
